/**
 * RepositoryResults-new.tsx
 * Redesigned Repository Results page.
 * Displays a two-column explorer/viewer layout for input and output file trees.
 * Design tokens align with Sidebar-new.css color scheme.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import
{
    ArrowClockwise,
    ChevronDown,
    ChevronRight,
    Download,
    FileEarmarkCode,
    Folder2Open,
    FolderFill,
} from 'react-bootstrap-icons';

import { useProjects } from '../contexts/ProjectContext';
import { ProjectWithRepository } from '../types/project';
import
{
    fetchRepositoryFileContent,
    fetchRepositoryResults,
    refreshRepositoryResults,
} from '../api/repositories';
import { RepositoryFileNode, RepositoryResultsPayload } from '../types/repository';
import CodeViewer from '../components/CodeViewer';
import { triggerDownloadFromBlob } from '../utils/download';
import apiClient, { API_ENDPOINTS } from '../api/config';
import './RepositoryResults-new.css';

/* ============================================================
   Local types
   ============================================================ */

/** Scope of file — whether it belongs to the input or output folder */
type Scope = 'input' | 'output';

/** Tracks the currently selected file for preview */
interface SelectedFile
{
    scope: Scope;
    path: string;
    name: string;
}

/* ============================================================
   FileTreeNodeItem — renders one file or directory row recursively
   ============================================================ */

/** Props for a single node in the file tree */
interface FileTreeNodeItemProps
{
    /** The node data */
    node: RepositoryFileNode;
    /** The scope (input | output) this node belongs to */
    scope: Scope;
    /** Accumulated path for computing the full node path */
    basePath: string;
    /** Currently selected file (if any) */
    selectedFile: SelectedFile | null;
    /** Record of expanded directory paths */
    expandedPaths: Record<string, boolean>;
    /** Called when a directory is toggled open/closed */
    onTogglePath: (scope: Scope, path: string) => void;
    /** Called when a file is selected for preview */
    onSelectFile: (scope: Scope, node: RepositoryFileNode) => void;
}

/**
 * FileTreeNodeItem
 * Renders a single tree node (file or directory) with optional children.
 * Recursion is achieved by rendering itself for each child.
 */
function FileTreeNodeItem(
    { node, scope, basePath, selectedFile, expandedPaths, onTogglePath, onSelectFile }: FileTreeNodeItemProps
): React.ReactElement
{
    /* Compute the canonical path for this node */
    const nodePath = node.path || (basePath ? `${basePath}/${node.name}` : node.name);
    const expandKey = `${scope}|${nodePath || '/'}`;
    const isDirectory = node.type === 'dir';
    const isExpanded = Boolean(expandedPaths[expandKey]);
    const isSelected = !isDirectory && selectedFile?.scope === scope && selectedFile.path === nodePath;

    /**
     * handleToggle
     * Toggles a directory open or closed.
     */
    function handleToggle(): void
    {
        onTogglePath(scope, nodePath);
    }

    /**
     * handleSelect
     * Selects a file node for preview in the viewer panel.
     */
    function handleSelect(): void
    {
        if (!isDirectory)
        {
            onSelectFile(scope, node);
        }
        else
        {
            onTogglePath(scope, nodePath);
        }
    }

    /**
     * handleKeyDown
     * Activates the row on Enter or Space key press for accessibility.
     */
    function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void
    {
        if (e.key === 'Enter' || e.key === ' ')
        {
            e.preventDefault();
            handleSelect();
        }
    }

    return (
        <li>
            {/* Row */}
            <div
                className={`repo-results-tree-row${isSelected ? ' repo-results-tree-row-selected' : ''}`}
                role={isDirectory ? 'button' : 'option'}
                aria-selected={isSelected}
                tabIndex={0}
                onClick={handleSelect}
                onKeyDown={handleKeyDown}
            >
                {/* Expand/collapse toggle for directories */}
                {isDirectory
                    ? (
                        <button
                            type="button"
                            className="repo-results-tree-toggle"
                            onClick={handleToggle}
                            aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
                        >
                            {isExpanded
                                ? <ChevronDown size={10} />
                                : <ChevronRight size={10} />
                            }
                        </button>
                    )
                    : <span style={{ width: 14, flexShrink: 0 }} />
                }

                {/* Node icon */}
                <span className="repo-results-tree-icon">
                    {isDirectory
                        ? (isExpanded
                            ? <Folder2Open size={13} />
                            : <FolderFill size={13} />
                        )
                        : <FileEarmarkCode size={13} />
                    }
                </span>

                {/* Node name */}
                <span className="repo-results-tree-name">{node.name}</span>

                {/* Analyzed tag for analyzed files */}
                {node.analyzed && (
                    <span className="repo-results-tree-analyzed-tag">Analyzed</span>
                )}
            </div>

            {/* Children — rendered when directory is expanded */}
            {isDirectory && isExpanded && node.children && node.children.length > 0 && (
                <ul className="repo-results-tree-nested">
                    {node.children.map((child) => (
                        <FileTreeNodeItem
                            key={`${scope}-${child.path || child.name}`}
                            node={child}
                            scope={scope}
                            basePath={nodePath}
                            selectedFile={selectedFile}
                            expandedPaths={expandedPaths}
                            onTogglePath={onTogglePath}
                            onSelectFile={onSelectFile}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
}

/* ============================================================
   ScopeSection — renders one Input/Output section in the explorer
   ============================================================ */

/** Props for the scope (input/output) section of the file explorer */
interface ScopeSectionProps
{
    /** Whether this section is the input or output scope */
    scope: Scope;
    /** Human-readable label */
    label: string;
    /** File tree nodes retrieved from the API */
    nodes: RepositoryFileNode[] | null | undefined;
    /** Whether the section's files are available on the server */
    available: boolean;
    /** Error message from the API (if any) */
    apiError: string | null | undefined;
    /** Currently selected file */
    selectedFile: SelectedFile | null;
    /** Record of expanded directory paths */
    expandedPaths: Record<string, boolean>;
    /** Called when a directory is toggled open/closed */
    onTogglePath: (scope: Scope, path: string) => void;
    /** Called when a file is selected for preview */
    onSelectFile: (scope: Scope, node: RepositoryFileNode) => void;
    /** Called when the download button is clicked */
    onDownload: (scope: Scope) => void;
}

/**
 * ScopeSection
 * Collapsible section showing either the Input or Output file tree.
 */
function ScopeSection(
    {
        scope,
        label,
        nodes,
        available,
        apiError,
        selectedFile,
        expandedPaths,
        onTogglePath,
        onSelectFile,
        onDownload,
    }: ScopeSectionProps
): React.ReactElement
{
    /* Local open/closed state for the section itself */
    const [open, setOpen] = useState<boolean>(true);

    /**
     * handleHeaderClick
     * Toggles the section open or closed.
     */
    function handleHeaderClick(): void
    {
        setOpen((prev) => !prev);
    }

    /**
     * handleDownloadClick
     * Triggers the zip download for this scope.
     * Stops event propagation to avoid toggling the section.
     */
    function handleDownloadClick(e: React.MouseEvent<HTMLButtonElement>): void
    {
        e.stopPropagation();
        onDownload(scope);
    }

    /**
     * handleScopeHeaderKeyDown
     * Activates the section toggle on Enter or Space for keyboard accessibility.
     */
    function handleScopeHeaderKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void
    {
        if (e.key === 'Enter' || e.key === ' ')
        {
            e.preventDefault();
            handleHeaderClick();
        }
    }

    return (
        <div className="repo-results-scope-section">

            {/* Section header — click to open/collapse */}
            <div
                className="repo-results-scope-header"
                onClick={handleHeaderClick}
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onKeyDown={handleScopeHeaderKeyDown}
            >
                <div className="repo-results-scope-title-row">
                    {/* Section label */}
                    <span className="repo-results-scope-label">{label}</span>

                    {/* Availability badge */}
                    <span
                        className={`repo-results-scope-badge ${available
                            ? 'repo-results-scope-badge-available'
                            : 'repo-results-scope-badge-unavailable'
                        }`}
                    >
                        {available ? 'Available' : 'Unavailable'}
                    </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {/* Download icon button */}
                    <button
                        type="button"
                        className="repo-results-scope-download-btn"
                        onClick={handleDownloadClick}
                        disabled={!available}
                        title={`Download ${label} zip`}
                    >
                        <Download size={13} />
                    </button>

                    {/* Chevron */}
                    <ChevronDown
                        size={12}
                        className={`repo-results-scope-chevron${open ? ' repo-results-scope-chevron-open' : ''}`}
                    />
                </div>
            </div>

            {/* Section body — only shown when open */}
            {open && (
                <div className="repo-results-scope-body">

                    {/* API error warning */}
                    {apiError && (
                        <Alert variant="warning" className="py-1 px-2 mb-2" style={{ fontSize: '0.78rem' }}>
                            {apiError}
                        </Alert>
                    )}

                    {/* File tree or empty state */}
                    {available && nodes && nodes.length > 0
                        ? (
                            <ul className="repo-results-tree">
                                {nodes.map((node) => (
                                    <FileTreeNodeItem
                                        key={`${scope}-${node.path || node.name}`}
                                        node={node}
                                        scope={scope}
                                        basePath=""
                                        selectedFile={selectedFile}
                                        expandedPaths={expandedPaths}
                                        onTogglePath={onTogglePath}
                                        onSelectFile={onSelectFile}
                                    />
                                ))}
                            </ul>
                        )
                        : (
                            <div className="repo-results-scope-empty">
                                No {label.toLowerCase()} files available.
                            </div>
                        )
                    }
                </div>
            )}
        </div>
    );
}

/* ============================================================
   Main page component
   ============================================================ */

/**
 * RepositoryResultsNew
 * Redesigned page showing input/output file trees and a live file viewer.
 * Supports downloading zip archives and refreshing cached results.
 */
function RepositoryResultsNew(): React.ReactElement
{
    const navigate = useNavigate();
    const { projectId } = useParams<{ projectId?: string }>();
    const numericProjectId = useMemo(() => Number(projectId ?? NaN), [projectId]);

    const { refreshProject } = useProjects();

    /* ---------- State ---------- */
    const [project, setProject] = useState<ProjectWithRepository | null>(null);
    const [projectLoading, setProjectLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<RepositoryResultsPayload | null>(null);
    const [loadingResults, setLoadingResults] = useState<boolean>(true);
    const [refreshingResults, setRefreshingResults] = useState<boolean>(false);
    const [expandedPaths, setExpandedPaths] = useState<Record<string, boolean>>({});
    const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
    const [fileContent, setFileContent] = useState<string>('');
    const [fileLoading, setFileLoading] = useState<boolean>(false);

    /* Derived values */
    const repositoryId = useMemo(() => project?.repository?.id ?? null, [project]);
    const repositoryName = project?.repository?.name ?? 'repository';

    /* ---- Data loading ---- */

    /**
     * loadProject
     * Fetches the project and its repository from the context.
     */
    const loadProject = useCallback(async () =>
    {
        if (!Number.isFinite(numericProjectId))
        {
            setError('Invalid project identifier.');
            setProjectLoading(false);
            return;
        }

        setProjectLoading(true);
        setError(null);

        try
        {
            const data = await refreshProject(numericProjectId);
            setProject(data ?? null);
        }
        catch (err: any)
        {
            const message = err?.response?.data?.detail || err?.message || 'Failed to load project';
            setError(message);
        }
        finally
        {
            setProjectLoading(false);
        }
    }, [numericProjectId, refreshProject]);

    /**
     * loadResults
     * Fetches (or refreshes) the repository file tree results from the API.
     */
    const loadResults = useCallback(async (options?: { refresh?: boolean }) =>
    {
        if (!repositoryId)
        {
            return;
        }

        if (options?.refresh)
        {
            setRefreshingResults(true);
        }
        else
        {
            setLoadingResults(true);
        }

        setError(null);

        try
        {
            const data = options?.refresh
                ? await refreshRepositoryResults(repositoryId)
                : await fetchRepositoryResults(repositoryId);
            setResults(data);
        }
        catch (err: any)
        {
            const message =
                err?.response?.data?.detail
                || err?.message
                || 'Failed to load repository results';
            setError(message);
        }
        finally
        {
            if (options?.refresh)
            {
                setRefreshingResults(false);
            }
            else
            {
                setLoadingResults(false);
            }
        }
    }, [repositoryId]);

    /* Trigger data loads on mount / when IDs change */
    useEffect(() =>
    {
        loadProject();
    }, [loadProject]);

    useEffect(() =>
    {
        if (repositoryId)
        {
            loadResults();
        }
    }, [repositoryId, loadResults]);

    /* ---- Event handlers ---- */

    /**
     * handleTogglePath
     * Toggles the expanded state of a directory node in the file tree.
     */
    const handleTogglePath = useCallback((scope: Scope, path: string) =>
    {
        setExpandedPaths((prev) =>
        {
            const key = `${scope}|${path || '/'}`;
            const next = { ...prev };
            next[key] = !next[key];
            return next;
        });
    }, []);

    /**
     * handleSelectFile
     * Loads the content of the selected file and updates the preview panel.
     */
    const handleSelectFile = useCallback(async (scope: Scope, node: RepositoryFileNode) =>
    {
        if (!repositoryId || !node.path)
        {
            return;
        }

        setSelectedFile({ scope, path: node.path, name: node.name });
        setFileLoading(true);

        try
        {
            const response = await fetchRepositoryFileContent(repositoryId, scope, node.path);
            setFileContent(response?.content || '');
        }
        catch (err: any)
        {
            const message =
                err?.response?.data?.detail
                || err?.message
                || 'Failed to load file content';
            setFileContent(message);
        }
        finally
        {
            setFileLoading(false);
        }
    }, [repositoryId]);

    /**
     * handleDownload
     * Triggers a zip download for either the input or output scope.
     */
    const handleDownload = useCallback(async (scope: Scope) =>
    {
        if (!repositoryId)
        {
            return;
        }

        try
        {
            const endpoint = scope === 'input'
                ? API_ENDPOINTS.REPOSITORY_DOWNLOAD_INPUT_ZIP(String(repositoryId))
                : API_ENDPOINTS.REPOSITORY_DOWNLOAD_OUTPUT_ZIP(String(repositoryId));

            const response = await apiClient.get(endpoint, { responseType: 'blob' });
            const filename = `${repositoryName}_${scope}.zip`;
            triggerDownloadFromBlob(response.data, filename);
        }
        catch (err: any)
        {
            const message =
                err?.response?.data?.detail
                || err?.message
                || `Failed to download ${scope} zip`;
            setError(message);
        }
    }, [repositoryId, repositoryName]);

    /**
     * handleRefresh
     * Re-fetches and refreshes the cached repository results from the server.
     */
    function handleRefresh(): void
    {
        loadResults({ refresh: true });
    }

    /**
     * handleBackToProject
     * Navigates back to the project detail page.
     */
    function handleBackToProject(): void
    {
        if (project)
        {
            navigate(`/projects/${project.id}`);
        }
    }

    /**
     * handleBreadcrumbProjects
     * Navigates to the projects listing page.
     */
    function handleBreadcrumbProjects(): void
    {
        navigate('/projects');
    }

    /**
     * handleBreadcrumbProject
     * Navigates to the current project's detail page.
     */
    function handleBreadcrumbProject(): void
    {
        if (project)
        {
            navigate(`/projects/${project.id}`);
        }
    }

    /* ---- Loading state ---- */

    if (projectLoading || loadingResults)
    {
        return (
            <div className="repo-results-loading">
                <Spinner animation="border" style={{ color: '#0f2a4a' }} />
            </div>
        );
    }

    /* ---- Error state ---- */

    if (error && !results)
    {
        return (
            <div className="repo-results-alert-wrap">
                <Alert variant="danger">
                    {error}
                    <div className="mt-2">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleBreadcrumbProjects}
                        >
                            Back to Projects
                        </Button>
                    </div>
                </Alert>
            </div>
        );
    }

    /* ---- Empty / unavailable state ---- */

    if (!project || !repositoryId || !results)
    {
        return (
            <div className="repo-results-alert-wrap">
                <Alert variant="warning">
                    Repository results are not available for this project.
                    <div className="mt-2">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleBreadcrumbProjects}
                        >
                            Back to Projects
                        </Button>
                    </div>
                </Alert>
            </div>
        );
    }

    /* ---- Derived scope data ---- */

    const inputScope = results.scopes.input;
    const outputScope = results.scopes.output;

    /* ---- Render ---- */

    return (
        <div className="repo-results-page">

            {/* Breadcrumb */}
            <nav className="repo-results-breadcrumb" aria-label="breadcrumb">
                <button
                    type="button"
                    className="repo-results-breadcrumb-item"
                    onClick={handleBreadcrumbProjects}
                >
                    Projects
                </button>

                <span className="repo-results-breadcrumb-sep">›</span>

                <button
                    type="button"
                    className="repo-results-breadcrumb-item"
                    onClick={handleBreadcrumbProject}
                >
                    {project.name}
                </button>

                <span className="repo-results-breadcrumb-sep">›</span>

                <span className="repo-results-breadcrumb-current">Repository Results</span>
            </nav>

            {/* Page header card */}
            <div className="repo-results-header">
                <div className="repo-results-title-block">
                    <h2 className="repo-results-title">Repository Results</h2>
                    <p className="repo-results-subtitle">{repositoryName}</p>
                </div>

                <div className="repo-results-actions">
                    {/* Back to project */}
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleBackToProject}
                        className="repo-results-back-btn"
                    >
                        Back to Project
                    </Button>

                    {/* Refresh cached results */}
                    <Button
                        size="sm"
                        onClick={handleRefresh}
                        disabled={refreshingResults}
                        className="repo-results-refresh-btn"
                    >
                        {refreshingResults
                            ? <Spinner animation="border" size="sm" />
                            : (
                                <>
                                    <ArrowClockwise size={13} className="me-1" />
                                    Refresh
                                </>
                            )
                        }
                    </Button>
                </div>
            </div>

            {/* Inline API error banner (non-fatal) */}
            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}

            {/* Two-column content layout */}
            <div className="repo-results-content">

                {/* Left panel — file explorer */}
                <div className="repo-results-explorer">

                    {/* Input files section */}
                    <ScopeSection
                        scope="input"
                        label="Input Files"
                        nodes={inputScope.tree}
                        available={inputScope.available}
                        apiError={inputScope.error}
                        selectedFile={selectedFile}
                        expandedPaths={expandedPaths}
                        onTogglePath={handleTogglePath}
                        onSelectFile={handleSelectFile}
                        onDownload={handleDownload}
                    />

                    {/* Output files section */}
                    <ScopeSection
                        scope="output"
                        label="Output Files"
                        nodes={outputScope.tree}
                        available={outputScope.available}
                        apiError={outputScope.error}
                        selectedFile={selectedFile}
                        expandedPaths={expandedPaths}
                        onTogglePath={handleTogglePath}
                        onSelectFile={handleSelectFile}
                        onDownload={handleDownload}
                    />
                </div>

                {/* Right panel — viewer + metadata */}
                <div className="repo-results-right-panel">

                    {/* File viewer card */}
                    <div className="repo-results-viewer-card">
                        <div className="repo-results-viewer-header">
                            <div>
                                <h3 className="repo-results-viewer-title">File Preview</h3>
                                {selectedFile && (
                                    <div className="repo-results-viewer-filepath">
                                        {selectedFile.path}
                                    </div>
                                )}
                            </div>

                            {/* Scope badge next to the title */}
                            {selectedFile && (
                                <span
                                    className={`repo-results-viewer-scope-badge ${selectedFile.scope === 'input'
                                        ? 'repo-results-viewer-scope-input'
                                        : 'repo-results-viewer-scope-output'
                                    }`}
                                >
                                    {selectedFile.scope}
                                </span>
                            )}
                        </div>

                        <div className="repo-results-viewer-body">
                            {/* Loading indicator */}
                            {fileLoading && (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                                    <Spinner animation="border" size="sm" />
                                </div>
                            )}

                            {/* File content */}
                            {!fileLoading && selectedFile && (
                                <CodeViewer
                                    filename={selectedFile.name || selectedFile.path}
                                    content={fileContent}
                                />
                            )}

                            {/* Placeholder when nothing is selected */}
                            {!fileLoading && !selectedFile && (
                                <div className="repo-results-viewer-empty">
                                    <FileEarmarkCode
                                        size={40}
                                        className="repo-results-viewer-empty-icon"
                                    />
                                    <p className="repo-results-viewer-empty-text">
                                        Select a file from the explorer to preview its contents.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Cache metadata card */}
                    <div className="repo-results-meta-card">
                        <h4 className="repo-results-meta-title">Cache Metadata</h4>

                        <div className="repo-results-meta-row">
                            <span className="repo-results-meta-key">Repository</span>
                            <span className="repo-results-meta-value">{repositoryName}</span>
                        </div>

                        <div className="repo-results-meta-row">
                            <span className="repo-results-meta-key">Last refreshed</span>
                            <span className="repo-results-meta-value">
                                {results.refreshed_at || '—'}
                            </span>
                        </div>

                        <div className="repo-results-meta-row">
                            <span className="repo-results-meta-key">Input updated</span>
                            <span className="repo-results-meta-value">
                                {inputScope.updated_at || '—'}
                            </span>
                        </div>

                        <div className="repo-results-meta-row">
                            <span className="repo-results-meta-key">Output updated</span>
                            <span className="repo-results-meta-value">
                                {outputScope.updated_at || '—'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RepositoryResultsNew;
